import {useContext, useEffect, useMemo, useState, useRef} from "react";
import {useNavigate, useParams, useLocation} from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
    Box, Button, Chip, CircularProgress, FormControl, Grid,
    IconButton, MenuItem, Paper, Select, TextField, Tooltip, Typography, Snackbar, Alert, Collapse
} from "@mui/material";
import {ArrowBack, Save, AutoFixHigh} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import {useHasScope} from "../../../../config/globalConfig";
import {getTemplate, getTemplateVersion, updateTemplate, activateTemplateVersion} from "../../api/templateApi";
import {Template, TemplateType, TemplateVersion, extractApiError, getErrorMessage} from "../../types";
import ActivateVersionDialog from "../../components/ActivateVersionDialog";
import ResultDialog from "../../components/ResultDialog";
import SaveConfirmDialog from "../../components/SaveConfirmDialog";
import CancelConfirmDialog from "../../components/CancelConfirmDialog";
import WysiwygEditor from "../../components/editor/WysiwygEditor";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {useHtmlFormatter} from "../../hooks/useHtmlFormatter";
import {TemplateValidationResult} from "../../components/editor/custom-email/types";
import {validateEmailTemplateHtml} from "../../components/editor/custom-email/utils/templateValidation";

dayjs.extend(utc);
const formatDate = (date: string) => dayjs.utc(date).local().format('DD-MM-YYYY HH:mm');
type EditorMode = "html" | "design";
type PreviewMode = "preview" | "nopreview";
type SaveMode = "overwrite" | "newVersion";

const channelColor: Record<TemplateType, "primary" | "secondary" | "success"> = {
    EMAIL: "primary",
    SMS: "secondary",
    PUSH: "success",
};

interface LocationState {
    version?: number | null;
}

const EditTemplate = () => {
    const {name} = useParams<{name: string}>();
    const location = useLocation();
    const navigate = useNavigate();
    const {t} = useTranslation("templates");
    const {globalData} = useContext(GlobalContext);
    const username: string = globalData?.user?.username || 'unknown';
    const canEdit = useHasScope("SCOPE_NOTIFICATION_HUB_TEMPLATE_EDIT");
    
    const { formatHtml, snackbarOpen, snackbarMessage, snackbarSeverity, closeSnackbar } = useHtmlFormatter();
    
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const lineNumbersRef = useRef<HTMLDivElement>(null);

    // Read version from navigation state
    const requestedVersion = (location.state as LocationState)?.version ?? null;

    const [template, setTemplate] = useState<Template | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saveResultOpen, setSaveResultOpen] = useState(false);
    const [saveResultSuccess, setSaveResultSuccess] = useState(false);
    const [saveResultErrorMessage, setSaveResultErrorMessage] = useState<string | undefined>(undefined);
    const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
    const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
    const [versions, setVersions] = useState<TemplateVersion[]>([]);
    const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
    const [activateOpen, setActivateOpen] = useState(false);
    const [activating, setActivating] = useState(false);
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [plainText, setPlainText] = useState("");
    const [saveMode, setSaveMode] = useState<SaveMode>("overwrite");
    const [editorMode, setEditorMode] = useState<EditorMode>("html");
    const [previewMode, setPreviewMode] = useState<PreviewMode>("preview");
    const [savedVersionNumber, setSavedVersionNumber] = useState<number | null>(null);
    const [customEditorValidation, setCustomEditorValidation] = useState<TemplateValidationResult>({ valid: true, errors: [] });

    const wrapInHtmlDoc = (content: string): string => {
        if (content.trim().toLowerCase().startsWith("<!doctype") || content.trim().toLowerCase().startsWith("<html")) return content;
        return `<!DOCTYPE html>\n<html lang="en">\n<head>\n    <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />\n</head>\n<body>\n${content}\n</body>\n</html>`;
    };
    const extractBodyContent = (html: string): string => {
        const match = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
        return match ? match[1].trim() : html.trim();
    };

    const isEmailBodyEmpty = (html: string): boolean => {
        if (!html) return true;
        const lower = html.toLowerCase();
        const openStart = lower.indexOf('<body');
        const closeStart = lower.indexOf('</body>');
        if (openStart === -1 || closeStart === -1 || closeStart < openStart) return true;
        const openEnd = html.indexOf('>', openStart);
        if (openEnd === -1 || openEnd >= closeStart) return true;
        const inner = html.substring(openEnd + 1, closeStart);
        const container = document.createElement('div');
        container.innerHTML = inner;
        const hasText = !!container.textContent?.trim();
        const hasVisibleElement = !!container.querySelector('img, hr, input, video, audio, iframe, svg, table');
        return !hasText && !hasVisibleElement;
    };

    const loadTemplate = (templateName: string, versionToLoad: number | null = null) => {
        setLoading(true);
        getTemplate(templateName).then((data) => {
            setTemplate(data); setError(null);
            const vers = (data.versions || []).sort((a, b) => b.versionNumber - a.versionNumber); setVersions(vers);
            
            // Determine which version to load
            let target: TemplateVersion | undefined;
            if (versionToLoad !== null) {
                // Try to load the requested version
                target = vers.find(v => v.versionNumber === versionToLoad);
            }
            if (!target) {
                // Fallback to active version or first version
                target = vers.find(v => v.isActive) || vers[0];
            }
            
            if (target) { setSelectedVersion(target.versionNumber); populateForm(data, target); }
        }).catch(() => setError(t('detailErrors.loadTemplate'))).finally(() => setLoading(false));
    };
    const populateForm = (tmpl: Template, ver: TemplateVersion) => {
        setSubject(ver.subjectTitle || "");
        tmpl.type === "EMAIL" ? setBody(wrapInHtmlDoc(ver.body || "")) : setPlainText(ver.body || "");
    };
    useEffect(() => { if (name) loadTemplate(name, requestedVersion); }, [name, requestedVersion]);

    const handleVersionChange = async (vn: number) => {
        if (!name || !template) return; setSelectedVersion(vn);
        const local = versions.find(v => v.versionNumber === vn);
        if (local?.body !== undefined) { populateForm(template, local); return; }
        setLoading(true);
        try { const d = await getTemplateVersion(name, vn); const v = d.versions?.find(x => x.versionNumber === vn); if (v) populateForm(template, v); }
        catch { setError(t('detailErrors.loadVersion')); } finally { setLoading(false); }
    };
    const handleActivateVersion = async () => {
        if (!name || selectedVersion === null) return; setActivating(true);
        try { await activateTemplateVersion(name, selectedVersion, username); setActivateOpen(false); loadTemplate(name); }
        catch { setError(t('detailErrors.activateVersion')); } finally { setActivating(false); }
    };
    const handleSave = async () => {
        if (!name || !template) return;
        const activeValidationAtSave = editorMode === "design"
            ? customEditorValidation
            : validateEmailTemplateHtml(extractBodyContent(body));
        if (template.type === "EMAIL" && !activeValidationAtSave.valid) {
            setSaveResultSuccess(false);
            setSaveResultErrorMessage("Cannot save: fix validation errors in the editor content first.");
            setSaveResultOpen(true);
            return;
        }
        // Subject (oggetto) is required for EMAIL and PUSH templates
        if ((template.type === "EMAIL" || template.type === "PUSH") && !subject.trim()) {
            setSaveResultSuccess(false);
            setSaveResultErrorMessage(t('createForm.requiredCheck'));
            setSaveResultOpen(true);
            return;
        }
        const finalBody = template.type === "EMAIL" ? extractBodyContent(body).trim() : plainText.trim();
        // Body is required for every template type
        if (!finalBody || (template.type === "EMAIL" && isEmailBodyEmpty(body))) {
            setSaveResultSuccess(false);
            setSaveResultErrorMessage(t('createForm.requiredCheck'));
            setSaveResultOpen(true);
            return;
        }
        setSaving(true); setError(null);
        try {
            await updateTemplate({ name: template.name, updatedBy: username, versions: [{
                versionNumber: selectedVersion ?? undefined,
                subjectTitle: (template.type === "EMAIL" || template.type === "PUSH") ? subject.trim() : undefined,
                body: finalBody }] }, saveMode === "newVersion");
            
            // Store the version number to navigate to after save
            if (saveMode === "newVersion") {
                // New version will be the highest version number + 1
                const maxVersion = Math.max(...versions.map(v => v.versionNumber), 0);
                setSavedVersionNumber(maxVersion + 1);
            } else {
                // Overwrite keeps the same version number
                setSavedVersionNumber(selectedVersion);
            }
            
            setSaveResultSuccess(true); setSaveResultOpen(true); loadTemplate(name);
        } catch (e: any) {
            setSaveResultSuccess(false);
            
            const apiError = extractApiError(e);
            if (apiError) {
                const errorMsg = getErrorMessage(apiError.code, t);
                setSaveResultErrorMessage(errorMsg);
            } else {
                setSaveResultErrorMessage(t('templates:errors.genericError'));
            }
            
            setSaveResultOpen(true);
        } finally { setSaving(false); }
    };

    const lineNumbers = useMemo(() => body.split("\n").map((_, i) => i + 1), [body]);
    const htmlModeValidation = useMemo<TemplateValidationResult>(() => {
        if (template?.type !== "EMAIL") return { valid: true, errors: [] };
        return validateEmailTemplateHtml(extractBodyContent(body));
    }, [body, template?.type]);
    
    const handleFormatHtml = () => {
        const formatted = formatHtml(body);
        if (formatted) {
            setBody(formatted);
        }
    };
    
    const handleScroll = (e: React.UIEvent<HTMLTextAreaElement>) => {
        if (lineNumbersRef.current && e.currentTarget) {
            lineNumbersRef.current.scrollTop = e.currentTarget.scrollTop;
        }
    };

    if (loading && !template) return <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}><CircularProgress/></Box>;
    
    if (error && !template) return (
        <>
            <SectionHeader title={t('detailHeader.errorTitle')} subtitle=""/>
            <Box sx={{p:'18px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300}}>
                <Box sx={{textAlign: 'center'}}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        {t('detailErrors.loadTemplate')}
                    </Typography>
                    <Button 
                        startIcon={<ArrowBack/>} 
                        sx={{mt:2}} 
                        variant="outlined"
                        onClick={()=>navigate("/notification-hub/list")}
                    >
                        {t('detailActions.backToList')}
                    </Button>
                </Box>
            </Box>
        </>
    );
    
    if (!template) return null;

    const activeVersion = versions.find(v => v.isActive);
    const isActiveVersion = selectedVersion !== null && activeVersion?.versionNumber === selectedVersion;
    const hasSubject = template.type === "EMAIL" || template.type === "PUSH";
    const isSubjectMissing = hasSubject && !subject.trim();
    const isBodyMissing = template.type === "EMAIL"
        ? isEmailBodyEmpty(body)
        : !plainText.trim();
    const activeEditorValidation = editorMode === "design" ? customEditorValidation : htmlModeValidation;
    const hasBlockingEditorErrors = template.type === "EMAIL" && !activeEditorValidation.valid;
    const hasEditorWarnings = !!activeEditorValidation.warnings?.length;

    return (<>
        <SectionHeader title={t('editHeader.label')} subtitle={
            <Box component="span" sx={{display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                {`${t('editHeader.templateName')}: ${template.name}`}
                {template.type && <Chip label={template.type} color={channelColor[template.type] || "default"} size="small"/>}
            </Box>
        }/>
        <Paper elevation={1} sx={{margin:'18px', padding:'24px'}}>
            <Grid container spacing={3}>
                {/* Version selector */}
                <Grid item xs={12}>
                    <Paper variant="outlined">
                        <Box sx={{px:2,py:1.5,display:'flex',alignItems:'center',gap:2,flexWrap:'wrap'}}>
                            <Box sx={{flexShrink:0}}>
                                <Typography variant="subtitle1" fontWeight={700}>{t('detailSection.versionLabel')}</Typography>
                                <Typography variant="body2" color="text.secondary">{t('editSection.subtitle')}</Typography>
                            </Box>
                            <FormControl size="small" sx={{width:420,maxWidth:'100%'}}>
                                <Select value={selectedVersion??""} onChange={e=>handleVersionChange(Number(e.target.value))}>
                                    {versions.map(v=>(<MenuItem key={v.versionNumber} value={v.versionNumber}><Box><Box sx={{display:'flex',alignItems:'center',gap:1}}><Typography variant="body2">{t('detailSection.versionItem',{number:v.versionNumber})}</Typography>{v.isActive&&<Chip label={t('detailSection.versionActive')} size="small" color="success" sx={{height:20,fontSize:'0.7rem'}}/>}</Box><Typography variant="caption" color="text.secondary">{t('detailSection.lastModified',{date:formatDate(v.updatedAt||v.createdAt||'')})}{v.updatedBy?` ${t('detailSection.by')} ${v.updatedBy}`:v.createdBy?` ${t('detailSection.by')} ${v.createdBy}`:''}</Typography></Box></MenuItem>))}
                                </Select>
                            </FormControl>
                            {canEdit&&!isActiveVersion&&selectedVersion!==null&&(<Button variant="contained" size="small" sx={{flexShrink:0}} onClick={()=>setActivateOpen(true)}>{t('detailActions.activateVersion')}</Button>)}
                        </Box>
                    </Paper>
                </Grid>

                {/* Content Section */}
                <Grid item xs={12}>
                    <Paper variant="outlined">
                        <Box sx={{px:2,py:1.5,borderBottom:'1px solid',borderColor:'divider'}}>
                            <Typography variant="subtitle1" fontWeight={700}>
                                {t('detailContent.content')}
                            </Typography>
                        </Box>
                        <Box sx={{px:2,py:2}}>
                            <Grid container spacing={2}>
                                {/* Subject */}
                                {hasSubject&&(<Grid item xs={12}><TextField fullWidth size="small" required label={template.type==="PUSH"?t('detailContent.pushTitleLabel'):t('detailContent.subject')} value={subject} onChange={e=>setSubject(e.target.value)} error={isSubjectMissing} helperText={isSubjectMissing ? "*" + t('createForm.requiredCheck') : undefined}/></Grid>)}

                                {/* Content */}
                                <Grid item xs={12}>
                                    {template.type==="EMAIL"?(
                                        <Box>
                                            <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'center',mb:1}}>
                                                <Typography variant="caption" color="text.secondary">{t('detailContent.bodyLabel')}</Typography>
                                                <Box sx={{display:'flex',gap:1,alignItems:'center'}}>
                                                    {editorMode === "html" && (
                                                        <Tooltip title={t('templates:createForm.formatHtmlTooltip')} arrow>
                                                            <IconButton size="small" onClick={handleFormatHtml} sx={{color: 'primary.main'}}>
                                                                <AutoFixHigh fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    <Select size="small" value={editorMode} onChange={e=>setEditorMode(e.target.value as EditorMode)} sx={{minWidth:100}}><MenuItem value="html">HTML</MenuItem><MenuItem value="design">Design</MenuItem></Select>
                                                    {editorMode==="html"&&(<Select size="small" value={previewMode} onChange={e=>setPreviewMode(e.target.value as PreviewMode)} sx={{minWidth:120}}><MenuItem value="preview">{t('detailContent.preview')}</MenuItem><MenuItem value="nopreview">{t('detailContent.noPreview')}</MenuItem></Select>)}
                                                </Box>
                                            </Box>
                                            {editorMode === "html" && (
                                                <>
                                                    <Collapse in={hasBlockingEditorErrors}>
                                                        <Alert severity="warning" sx={{ borderRadius: 0, fontSize: '0.8rem', mb: 1 }}>
                                                            {activeEditorValidation.errors.map((err, i) => (
                                                                <div key={i}>{err}</div>
                                                            ))}
                                                        </Alert>
                                                    </Collapse>
                                                    <Collapse in={!hasBlockingEditorErrors && hasEditorWarnings}>
                                                        <Alert severity="info" sx={{ borderRadius: 0, fontSize: '0.8rem', mb: 1 }}>
                                                            {activeEditorValidation.warnings?.map((warning, i) => (
                                                                <div key={i}>{warning}</div>
                                                            ))}
                                                        </Alert>
                                                    </Collapse>
                                                </>
                                            )}
                                            <Paper variant="outlined" sx={{borderColor: isBodyMissing ? 'error.main' : undefined}}>
                                                <Box sx={{display:'flex',minHeight:400,maxHeight:700,transition:'min-height 0.2s'}}>
                                                    <Box sx={{display: editorMode === "html" ? 'flex' : 'none', flex: 1}}>
                                                        <Box
                                                            ref={lineNumbersRef}
                                                            sx={{
                                                                bgcolor:'grey.100',
                                                                borderRight:'1px solid',
                                                                borderColor:'divider',
                                                                px:1,
                                                                py:1,
                                                                fontFamily:'monospace',
                                                                fontSize:13,
                                                                lineHeight:'20px',
                                                                color:'text.secondary',
                                                                userSelect:'none',
                                                                textAlign:'right',
                                                                minWidth:40,
                                                                overflow:'hidden'
                                                            }}
                                                        >
                                                            {lineNumbers.map(n => <div key={n}>{n}</div>)}
                                                        </Box>
                                                        <Box
                                                            component="textarea"
                                                            ref={textareaRef}
                                                            value={body}
                                                            onChange={(e:any) => setBody(e.target.value)}
                                                            onScroll={handleScroll}
                                                            spellCheck={false}
                                                            sx={{
                                                                flex:1,
                                                                border:'none',
                                                                outline:'none',
                                                                resize:'none',
                                                                fontFamily:'monospace',
                                                                fontSize:13,
                                                                lineHeight:'20px',
                                                                p:1,
                                                                overflow:'auto',
                                                                bgcolor:'background.paper'
                                                            }}
                                                        />
                                                    </Box>
                                                    <Box sx={{display: editorMode === "design" ? 'flex' : 'none', flex:1, flexDirection:'column', minHeight:400}}>
                                                        <WysiwygEditor
                                                            engine="custom"
                                                            initialHtml={extractBodyContent(body)}
                                                            onChange={html => setBody(wrapInHtmlDoc(html))}
                                                            onValidationChange={setCustomEditorValidation}
                                                        />
                                                    </Box>
                                                    {editorMode === "html" && previewMode === "preview" && (
                                                        <Box sx={{flex:1,borderLeft:'1px solid',borderColor:'divider',overflow:'hidden',bgcolor:'#fff'}}>
                                                            <iframe
                                                                title="preview"
                                                                srcDoc={body}
                                                                style={{width:'100%',height:'100%',border:'none',minHeight:400}}
                                                                sandbox="allow-same-origin"
                                                            />
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Paper>
                                            {isBodyMissing && (
                                                <Typography variant="caption" color="error" sx={{mt: 0.5, display: 'block'}}>
                                                    {"*" + t('createForm.requiredCheck')}
                                                </Typography>
                                            )}
                                        </Box>
                                    ):(<TextField fullWidth multiline required minRows={1} maxRows={15} label={t('detailContent.content')} value={plainText} onChange={e=>setPlainText(e.target.value)} error={isBodyMissing} helperText={isBodyMissing ? "*" + t('createForm.requiredCheck') : undefined}/>)}
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Paper>
        <Box sx={{display:'flex',justifyContent:'flex-end',alignItems:'center',gap:2,mx:'18px',mb:3}}>
            <Button variant="outlined" onClick={()=>setCancelConfirmOpen(true)}>{t('actions.cancel')}</Button>
            <Button variant="contained" color="accent" startIcon={<Save/>} disabled={saving || hasBlockingEditorErrors || isSubjectMissing || isBodyMissing} onClick={()=>{setSaveMode('overwrite');setSaveConfirmOpen(true);}}>{t('editSave.overwrite')}</Button>
            <Button variant="contained" color="accent" startIcon={<Save/>} disabled={saving || hasBlockingEditorErrors || isSubjectMissing || isBodyMissing} onClick={()=>{setSaveMode('newVersion');setSaveConfirmOpen(true);}}>{t('editSave.newVersion')}</Button>
        </Box>
        <ActivateVersionDialog open={activateOpen} versionNumber={selectedVersion} onClose={()=>setActivateOpen(false)} onConfirm={handleActivateVersion} loading={activating}/>
        <SaveConfirmDialog open={saveConfirmOpen} isNewVersion={saveMode==="newVersion"} onClose={()=>setSaveConfirmOpen(false)} onConfirm={()=>{setSaveConfirmOpen(false);handleSave();}} loading={saving}/>
        <CancelConfirmDialog open={cancelConfirmOpen} onClose={()=>setCancelConfirmOpen(false)} onConfirm={()=>{
            navigate(`/notification-hub/view/${name}`, {
                state: { version: selectedVersion }
            });
        }}/>
        <ResultDialog open={saveResultOpen} success={saveResultSuccess} errorMessage={saveResultErrorMessage} onClose={()=>{
            setSaveResultOpen(false);
            if (saveResultSuccess && name && savedVersionNumber !== null) {
                navigate(`/notification-hub/view/${name}`, {
                    state: { version: savedVersionNumber }
                });
            }
        }}/>
        <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={closeSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
            <Alert onClose={closeSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
                {snackbarMessage}
            </Alert>
        </Snackbar>
    </>);
};

export default EditTemplate;
